/**
 * NewJobPost — recruiter creates a job listing.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Briefcase, ArrowLeft, Loader2, MapPin, Banknote, Save, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function NewJobPost() {
  const navigate = useNavigate();
  const [facets, setFacets] = useState(null);
  const [form, setForm] = useState({
    role_title: "",
    org_name: "",
    category: "",
    is_extra_curricular: false,
    subject: "",
    grade_levels: [],
    job_type: "full_time",
    mode: "offline",
    location: "",
    salary_min: "",
    salary_max: "",
    experience_required: "",
    qualification: "",
    curriculum: "",
    description: "",
    deadline: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/marketplace/facets").then((r) => setFacets(r.data)).catch(() => {});
    api.get("/marketplace/hiring/me").then((r) => setForm((p) => ({ ...p, org_name: r.data.org_name || "" }))).catch(() => {});
  }, []);

  const set = (patch) => setForm((p) => ({ ...p, ...patch }));

  const submit = async () => {
    if (!form.role_title.trim()) { toast.error("Add a role title"); return; }
    if (!form.description.trim()) { toast.error("Add a job description"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        salary_min: form.salary_min ? parseInt(form.salary_min, 10) : null,
        salary_max: form.salary_max ? parseInt(form.salary_max, 10) : null,
        experience_required: form.experience_required ? parseInt(form.experience_required, 10) : null,
      };
      const { data } = await api.post("/marketplace/jobs", payload);
      toast.success("Job posted", { description: data.role_title });
      navigate("/hiring/jobs");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post job");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout title="Post a Job">
      <Button variant="ghost" size="sm" onClick={() => navigate("/hiring")} className="rounded-full mb-3" data-testid="newjob-back">
        <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back
      </Button>

      <div className="tb-card-feature relative overflow-hidden mb-5">
        <div className="absolute inset-0 tb-grid-bg opacity-40 pointer-events-none" />
        <div className="relative">
          <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
            <Briefcase className="h-3 w-3" /> New job post
          </span>
          <h1 className="mt-3 font-display text-2xl sm:text-3xl tracking-tight font-medium">Reach educators ready to teach</h1>
          <p className="mt-1.5 text-sm text-muted-foreground max-w-xl">Fill the essentials below. Teachers see only what's helpful; you can edit anytime.</p>
        </div>
      </div>

      <div className="tb-card p-5 space-y-4" data-testid="newjob-form">
        <Field label="Role title *" testid="newjob-role">
          <input className="tb-input" value={form.role_title} onChange={(e) => set({ role_title: e.target.value })} placeholder="e.g. CBSE Maths Teacher · Grade 9–10" />
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Institution name" testid="newjob-org">
            <input className="tb-input" value={form.org_name} onChange={(e) => set({ org_name: e.target.value })} placeholder="Your school or institution" />
          </Field>
          <Field label="Category" testid="newjob-cat">
            <select className="tb-input" value={form.category} onChange={(e) => set({ category: e.target.value })}>
              <option value="">Choose</option>
              {facets && [...facets.curricular_categories, "—", ...facets.extra_curricular_categories].map((c) =>
                c === "—" ? <option key="sep" disabled>──────────</option> : <option key={c} value={c}>{c}</option>
              )}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Job type" testid="newjob-type">
            <select className="tb-input" value={form.job_type} onChange={(e) => set({ job_type: e.target.value })}>
              {(facets?.job_types || []).map((jt) => <option key={jt} value={jt}>{jt.replace(/_/g, " ")}</option>)}
            </select>
          </Field>
          <Field label="Mode" testid="newjob-mode">
            <select className="tb-input" value={form.mode} onChange={(e) => set({ mode: e.target.value })}>
              {(facets?.modes || []).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Location" icon={MapPin} testid="newjob-location">
            <input className="tb-input" value={form.location} onChange={(e) => set({ location: e.target.value })} placeholder="City, country" />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Salary min (INR/mo)" icon={Banknote} testid="newjob-sal-min">
            <input className="tb-input" type="number" min={0} value={form.salary_min} onChange={(e) => set({ salary_min: e.target.value })} />
          </Field>
          <Field label="Salary max (INR/mo)" icon={Banknote} testid="newjob-sal-max">
            <input className="tb-input" type="number" min={0} value={form.salary_max} onChange={(e) => set({ salary_max: e.target.value })} />
          </Field>
          <Field label="Experience required (yrs)" testid="newjob-exp">
            <input className="tb-input" type="number" min={0} value={form.experience_required} onChange={(e) => set({ experience_required: e.target.value })} />
          </Field>
        </div>

        <Field label="Job description *" testid="newjob-desc">
          <textarea className="tb-input" rows={6} value={form.description} onChange={(e) => set({ description: e.target.value })}
                    placeholder="What will the teacher do, who you are, what makes this special" />
        </Field>

        <div className="flex items-center justify-between gap-2 pt-3 border-t border-border/60">
          <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> Teachers see only the fields you fill in.</p>
          <Button onClick={submit} disabled={saving} className="rounded-full" data-testid="newjob-submit">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Post job
          </Button>
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, icon: Icon, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground inline-flex items-center gap-1.5">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </span>
      <span className="block mt-1.5">{children}</span>
    </label>
  );
}
